import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { IpcRest } from "@/hooks/ipc";
import { billingSummarySchema, type BillingSummary } from "@/schemas/billing";
import { esSinSuscripcion } from "./clasificarError";
import { useBillingScope } from "./useBillingScope";

/** La clave de react-query del resumen. Se invalida al pagar. */
export const BILLING_QUERY_KEY = ["business-billing"];

export type EstadoDeBilling =
  | { estado: "cargando" }
  | { estado: "sin-suscripcion" }
  | { estado: "error"; error: unknown }
  | { estado: "listo"; resumen: BillingSummary };

/**
 * El resumen de billing del negocio: estado, plan, medio de pago, saldo y el
 * pendiente abierto más viejo.
 *
 * Devuelve una unión discriminada en vez de `data | undefined` para que el
 * call-site no pueda confundir "todavía no cargó", "no hay suscripción" y
 * "falló la llamada" — que en pantalla son tres textos distintos.
 *
 * `habilitado` existe porque es el carril del **dueño** (`role=owner`): desde
 * la banda de mora, que vive en el layout de todas las pantallas, pedirlo para
 * un empleado es un 403 en cada navegación. Deshabilitado queda en
 * `cargando` y no pega.
 */
export function useBusinessBilling({
  habilitado = true,
}: { habilitado?: boolean } = {}) {
  const navigate = useNavigate();
  const scope = useBillingScope();

  const query = useQuery<BillingSummary>(
    [...BILLING_QUERY_KEY, ...scope],
    async () => {
      const data = await IpcRest.get<unknown>(
        { url: "/api/v1/business/billing" },
        navigate
      );
      return billingSummarySchema.parse(data);
    },
    {
      enabled: habilitado,
      staleTime: 30_000,
      refetchOnMount: "always",
      refetchOnWindowFocus: "always",
      // Reintentar un "no tiene suscripción" es pegarle cuatro veces al backend
      // para que conteste lo mismo.
      retry: (intentos, error) => !esSinSuscripcion(error) && intentos < 2,
    }
  );

  const estado: EstadoDeBilling = query.isLoading
    ? { estado: "cargando" }
    : query.isError
    ? esSinSuscripcion(query.error)
      ? { estado: "sin-suscripcion" }
      : { estado: "error", error: query.error }
    : query.data
    ? { estado: "listo", resumen: query.data }
    : { estado: "cargando" };

  return { ...estado, refetch: query.refetch, isFetching: query.isFetching };
}
